import { useQuery } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import type { FC } from "react";
import React from "react";

import { getUserPresentableMessage } from "@core/foundation/error";
import { bindArgs } from "@core/foundation/function";

import { ConsentPrompt } from "@react/domains/oauth-consent";
import { ErrorView, LoadingView } from "@react/foundation/placeholder";

import type { OAuthRequestId } from "@apis/account/models";

import { PageView } from "../../../shared/components/PageView.tsx";
import { useQueries } from "../../../shared/hooks/useQueries.ts";
import { useOAuthConsentActions } from "../hooks/useOAuthConsentActions.ts";

interface ConsentRequestProps {
  readonly id: OAuthRequestId;
}

export const ConsentRequest: FC<ConsentRequestProps> = ({ id }) => {
  const { getOAuthRequestQueryOptions } = useQueries();
  const oauthRequest = useQuery(getOAuthRequestQueryOptions({ id }));

  const { approve, deny } = useOAuthConsentActions();

  if (oauthRequest.status === "pending") {
    return <LoadingView />;
  }

  if (oauthRequest.status === "error") {
    return (
      <ErrorView
        description={getUserPresentableMessage(
          oauthRequest.error,
          "Failed to load the authorization request.",
        )}
      />
    );
  }

  return (
    <PageView
      icon={<ShieldCheck />}
      subtitle="Review what this client is asking for."
      title="Authorize application"
    >
      <ConsentPrompt
        client={oauthRequest.data.client}
        isPending={approve.isPending || deny.isPending}
        onApprove={bindArgs(approve.mutate, { id })}
        onDeny={bindArgs(deny.mutate, { id })}
        request={oauthRequest.data.request}
      />
    </PageView>
  );
};
